import axios from "axios";

export type CalendarAlbum = {
    calendar_date: string | Date;
    album_id: number | string;
    album_name: string;
    artist_name: string;
    album_link: string;
    recommender_name: string;
    cover_image_url?: string;
};

export type Album = {
    id: number;
    source: string;
    source_id: string;
    artist_source_id: string;
    artist_name: string;
    source_url: string;
    source_cover_url: string | null;
    title: string;
    in_library?: boolean;
};

/** Search result — `id` is set when the album is already in SingleStore. */
export type SearchAlbumResult = Omit<Album, "id"> & {
    id?: number;
    in_library: boolean;
};

export type RecommendationStatus = "queue" | "skipped" | "done" | "rejected";

export type Recommendation = {
    id: number;
    from_user_id: number;
    to_user_id: number;
    album_id: number;
    status: RecommendationStatus;
    created_on: string;
    updated_on: string;
    source: string;
    source_id: string;
    title: string;
    artist_name: string;
    source_url: string;
    source_cover_url: string | null;
};

export type RecommendAlbumPayload = {
    to_user_id: string;
    album_id: number;
};

export type RecommendationDirection = "all" | "sent" | "received";

export type EnsureAlbumPayload = Omit<SearchAlbumResult, "id" | "in_library">;

export const fetchAlbums = async () => {
    try {
        const response = await axios.get<Array<CalendarAlbum>>("/api/albums");
        return response.data;
    } catch (error) {
        console.error("Error fetching albums:", error);
    }
};

export type AlbumSearchResponse = {
    results: SearchAlbumResult[];
    meta: {
        library_count: number;
        spotify_configured: boolean;
        spotify_error?: string;
    };
};

export const searchAlbums = async (query: string) => {
    const response = await axios.get<AlbumSearchResponse>("/api/albums/search", {
        params: { q: query },
    });
    return response.data;
};

export const fetchAlbumById = async (albumId: string) => {
    const response = await axios.get<Album>("/api/album/detail", {
        params: { id: albumId },
    });
    return response.data;
};

export const ensureAlbum = async (album: EnsureAlbumPayload) => {
    const response = await axios.post<Album>("/api/album/ensure", album);
    return response.data;
};

export const recommendAlbum = async (payload: RecommendAlbumPayload) => {
    const response = await axios.post<Recommendation>("/api/albums/recommend", payload);
    return response.data;
};

export type ListenTabData = {
    queue_next: Recommendation | null;
    listened_today: Recommendation | null;
    history: Recommendation[];
};

export const fetchListenTabData = async (userId: string) => {
    const response = await axios.get<ListenTabData>("/api/albums/recommendations", {
        params: {
            user_id: userId,
            view: "listen",
        },
    });
    return response.data;
};

export const markAlbumAsListened = async (albumId: number) => {
    const response = await axios.post<Recommendation>("/api/albums/recommendations", {
        album_id: albumId,
    });
    return response.data;
};

export const fetchRecommendations = async (
    userId: string,
    options: { direction?: RecommendationDirection; status?: RecommendationStatus } = {}
) => {
    const response = await axios.get<Array<Recommendation>>("/api/albums/recommendations", {
        params: {
            user_id: userId,
            ...(options.direction ? { direction: options.direction } : {}),
            ...(options.status ? { status: options.status } : {}),
        },
    });
    return response.data;
};

export type FriendExchangeHistory = {
    received: Recommendation[];
    sent: Recommendation[];
};

export const fetchFriendExchangeHistory = async (
    friendUserId: string,
    authToken: string | null
) => {
    const response = await axios.get<FriendExchangeHistory>("/api/albums/recommendations", {
        params: {
            view: "friend-exchange",
            friend_id: friendUserId,
        },
        ...(authToken ? { headers: { Authorization: `Bearer ${authToken}` } } : {}),
    });
    return response.data;
};

export const fetchAlbumListenedByUserIds = async (albumId: number) => {
    const response = await axios.get<{ user_ids: string[] }>("/api/albums/listened-by", {
        params: { album_id: albumId },
    });
    return response.data.user_ids;
};

export const fetchAlbumStats = async (userId: string) => {
    const response = await axios.get<{
        shared_album_count: number;
        listened_album_count: number;
    }>("/api/albums/stats", {
        params: { user_id: userId },
    });
    return response.data;
};

export function searchPath(query?: string) {
    const trimmed = query?.trim();
    if (!trimmed) {
        return "/search";
    }
    return `/search?q=${encodeURIComponent(trimmed)}`;
}

export function albumPath(albumId: number, searchQuery?: string) {
    const base = `/album/${albumId}`;
    const trimmed = searchQuery?.trim();
    if (!trimmed) {
        return base;
    }
    return `${base}?q=${encodeURIComponent(trimmed)}`;
}
